import type * as Types from '../generated-base';

import type { DocumentNode } from 'graphql';
import * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@graphql/react-hooks';
export type PublicShopRootQueryVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
  includeEvent: Types.Scalars['Boolean'];
}>;

export type PublicShopRootQuery = {
  __typename?: 'Query';
  eventById?: {
    __typename?: 'Event';
    id: string;
    website: string;
    info: {
      __typename?: 'EventInfo';
      id: string;
      eventType: Types.EventType;
      registry: Array<{
        __typename?: 'Registry';
        id: string;
        nameV0204?: string | null;
        urlV0204?: string | null;
        imageUrlV0204?: string | null;
        wantSync?: boolean | null;
        type: Types.RegistryType;
        isStandalonesType: boolean;
        isSyncingType: boolean;
        isTransferredType: boolean;
        storeName?: string | null;
        cannotSync: boolean;
        hasItems: boolean;
        transferredRegistryData?: {
          __typename?: 'TransferredRegistryData';
          transferredAt: { __typename?: 'Date'; dateString: string; timestamp: any; timezone: string; milliseconds: number };
          lastPublicCheck?: {
            __typename?: 'ExternalRegistryStatusData';
            isPublic: boolean;
            checkedAt: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number };
          } | null;
        } | null;
        lastSyncAt?: { __typename?: 'Date'; timestamp: any } | null;
        items?: Array<{
          __typename?: 'RegistryItem';
          id: string;
          isGroupGiftingEnabled?: boolean | null;
          isGetCashEquivalent?: boolean | null;
          note?: string | null;
          externallyOwned: boolean;
          canEditProductData: boolean;
          storeName?: string | null;
          fpIndex: number;
          stillNeeded: number;
          alreadyPurchased?: number | null;
          alreadyReserved?: number | null;
          totalRequested?: number | null;
          isHidden: boolean;
          mustHave: boolean;
          createAt: { __typename?: 'Date'; milliseconds: number; timestamp: any; timezone: string };
          productData: {
            __typename?: 'RegistryItemProductData';
            brand?: string | null;
            customType?: Types.CustomItemDataType | null;
            title: string;
            externalUrl?: string | null;
            checkoutUrl?: string | null;
            pdpUrl?: string | null;
            description?: string | null;
            isFullPrice: boolean;
            legacyType: Types.LegacyRegistryItemType;
            registryItem: { __typename?: 'RegistryItem'; id: string };
            price?: {
              __typename?: 'RegistryMonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'RegistryCurrency'; id: string; code: string; places: number } | null;
            } | null;
            fullPrice?: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            } | null;
            photos: Array<{ __typename?: 'Photo'; id: string; url: string }>;
          };
          donationFund?: {
            __typename?: 'DonationFund';
            id: string;
            fundType: Types.CustomItemDataType;
            suggestedQuantityStillNeeded?: number | null;
            donationsAreCapped: boolean;
            enforceSuggestedDonationAmount: boolean;
            hideFundingGoal: boolean;
            progressToGoalPercent?: number | null;
            fpIndex?: number | null;
            title: string;
            url?: string | null;
            description?: string | null;
            enableCashOrCheck?: boolean | null;
            enableAppPlatform: boolean;
            enableJoyCredit: boolean;
            mustHave: boolean;
            donations: Array<{
              __typename?: 'DonationFundDonation';
              id: string;
              name: string;
              email: string;
              note?: string | null;
              platformType?: Types.DonationFundPaymentMethodEnum | null;
              amount: {
                __typename?: 'MonetaryValue';
                valueInMinorUnits: number;
                floatingPointDecimalString: string;
                currency?: { __typename?: 'Currency'; code: string; places: number } | null;
              };
              orderedAt: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number };
            }>;
            goalMonetaryValue?: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            } | null;
            suggestedDonationMonetaryValue?: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            } | null;
            platform?: {
              __typename?: 'DonationFundPlatform';
              id?: string | null;
              type: Types.DonationFundPlatformTypeEnum;
              name: string;
              link?: string | null;
              username?: string | null;
            } | null;
            photos: Array<{ __typename?: 'Photo'; id: string; url: string }>;
            progressToGoal?: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            } | null;
            amountTillGoalIsReached?: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            } | null;
            currency: { __typename?: 'RegistryCurrency'; code: string; id: string; displayName: string };
          } | null;
        }> | null;
        myReservedOrders: Array<{
          __typename?: 'RegistryOrder';
          id: string;
          name: string;
          email: string;
          note?: string | null;
          isPurchased: boolean;
          isDropship: boolean;
          status?: string | null;
          orderTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
          orderedAt: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number };
          lineItems: Array<{
            __typename?: 'LineItem';
            locator: string;
            quantity: number;
            isGetCashEquivalent: boolean;
            lineTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
            registryItem?: { __typename?: 'RegistryItem'; id: string } | null;
            lineItemData: { __typename?: 'RegistryOrderLineItemData'; donationFundPlatformType?: Types.DonationFundPaymentMethodEnum | null; correctedQuantity: number };
            annotations: { __typename?: 'RegistryOrderLineItemAnnotations'; isEdelivery?: boolean | null };
            frozenProductData: {
              __typename?: 'RegistrySummaryProductData';
              title: string;
              customType: Types.CustomItemDataType;
              price: {
                __typename?: 'RegistryMonetaryValue';
                valueInMinorUnits: number;
                floatingPointDecimalString: string;
                currency?: { __typename?: 'RegistryCurrency'; id: string; code: string; places: number } | null;
              };
              photos: Array<{ __typename?: 'Photo'; id: string; url: string }>;
            };
          }>;
          fulfillment?: {
            __typename?: 'RegistryFulfillment';
            status: string;
            statusPageUrl?: string | null;
            orderNumber: string;
            shipments?: Array<{
              __typename?: 'RegistryFulfillmentShipment';
              trackingInformation?: {
                __typename?: 'RegistryFulfillmentShipmentTrackingInformation';
                url?: string | null;
                company?: string | null;
                trackingNumber?: string | null;
              } | null;
            }> | null;
          } | null;
          shippingAddress?: {
            __typename?: 'RegistryOrderShippingAddress';
            hash?: string | null;
            name?: string | null;
            address1?: string | null;
            address2?: string | null;
            city?: string | null;
            state?: string | null;
            postalCode?: string | null;
            country?: string | null;
            countryCode?: string | null;
          } | null;
        } | null>;
        myPurchasedOrders: Array<{
          __typename?: 'RegistryOrder';
          id: string;
          name: string;
          email: string;
          note?: string | null;
          isPurchased: boolean;
          isDropship: boolean;
          status?: string | null;
          orderTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
          orderedAt: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number };
          lineItems: Array<{
            __typename?: 'LineItem';
            locator: string;
            quantity: number;
            isGetCashEquivalent: boolean;
            lineTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
            registryItem?: { __typename?: 'RegistryItem'; id: string } | null;
            lineItemData: { __typename?: 'RegistryOrderLineItemData'; donationFundPlatformType?: Types.DonationFundPaymentMethodEnum | null; correctedQuantity: number };
            annotations: { __typename?: 'RegistryOrderLineItemAnnotations'; isEdelivery?: boolean | null };
            frozenProductData: {
              __typename?: 'RegistrySummaryProductData';
              title: string;
              customType: Types.CustomItemDataType;
              price: {
                __typename?: 'RegistryMonetaryValue';
                valueInMinorUnits: number;
                floatingPointDecimalString: string;
                currency?: { __typename?: 'RegistryCurrency'; id: string; code: string; places: number } | null;
              };
              photos: Array<{ __typename?: 'Photo'; id: string; url: string }>;
            };
          }>;
          fulfillment?: {
            __typename?: 'RegistryFulfillment';
            status: string;
            statusPageUrl?: string | null;
            orderNumber: string;
            shipments?: Array<{
              __typename?: 'RegistryFulfillmentShipment';
              trackingInformation?: {
                __typename?: 'RegistryFulfillmentShipmentTrackingInformation';
                url?: string | null;
                company?: string | null;
                trackingNumber?: string | null;
              } | null;
            }> | null;
          } | null;
          shippingAddress?: {
            __typename?: 'RegistryOrderShippingAddress';
            hash?: string | null;
            name?: string | null;
            address1?: string | null;
            address2?: string | null;
            city?: string | null;
            state?: string | null;
            postalCode?: string | null;
            country?: string | null;
            countryCode?: string | null;
          } | null;
        } | null>;
        mySession?: { __typename?: 'RegistrySession'; email: string } | null;
      }>;
      finalizedDate?: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number } | null;
    };
    registry: {
      __typename?: 'EventData';
      id: string;
      orders?: Array<{
        __typename?: 'RegistryOrder';
        id: string;
        name: string;
        email: string;
        note?: string | null;
        isPurchased: boolean;
        isDropship: boolean;
        status?: string | null;
        orderTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
        orderedAt: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number };
        lineItems: Array<{
          __typename?: 'LineItem';
          locator: string;
          quantity: number;
          isGetCashEquivalent: boolean;
          lineTags: Array<{ __typename?: 'CommerceTag'; key: string; value: string }>;
          registryItem?: { __typename?: 'RegistryItem'; id: string } | null;
          lineItemData: { __typename?: 'RegistryOrderLineItemData'; donationFundPlatformType?: Types.DonationFundPaymentMethodEnum | null; correctedQuantity: number };
          annotations: { __typename?: 'RegistryOrderLineItemAnnotations'; isEdelivery?: boolean | null };
          frozenProductData: {
            __typename?: 'RegistrySummaryProductData';
            title: string;
            customType: Types.CustomItemDataType;
            price: {
              __typename?: 'RegistryMonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'RegistryCurrency'; id: string; code: string; places: number } | null;
            };
            photos: Array<{ __typename?: 'Photo'; id: string; url: string }>;
          };
        }>;
        fulfillment?: {
          __typename?: 'RegistryFulfillment';
          status: string;
          statusPageUrl?: string | null;
          orderNumber: string;
          shipments?: Array<{
            __typename?: 'RegistryFulfillmentShipment';
            trackingInformation?: {
              __typename?: 'RegistryFulfillmentShipmentTrackingInformation';
              url?: string | null;
              company?: string | null;
              trackingNumber?: string | null;
            } | null;
          }> | null;
        } | null;
        shippingAddress?: {
          __typename?: 'RegistryOrderShippingAddress';
          hash?: string | null;
          name?: string | null;
          address1?: string | null;
          address2?: string | null;
          city?: string | null;
          state?: string | null;
          postalCode?: string | null;
          country?: string | null;
          countryCode?: string | null;
        } | null;
      }> | null;
      shippingAddress?: {
        __typename?: 'ShippingAddress';
        name?: string | null;
        address1: string;
        address2?: string | null;
        phone?: string | null;
        city: string;
        state: string;
        postalCode?: string | null;
        country: string;
        countryCode?: string | null;
        validated: boolean;
        hash?: string | null;
      } | null;
      currency?: { __typename?: 'RegistryCurrency'; isDefault: boolean; id: string; displayName: string; code: string; rank?: number | null } | null;
    };
  } | null;
};

export const PublicShopRootDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'PublicShopRoot' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeEvent' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'if' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'includeEvent' } } }],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'website' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryShippingAddressFragment' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'info' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'registry' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Registry' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'finalizedDate' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'registry' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'orders' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryOrder' } }] },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryShippingAddressFragment' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Event' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registry' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'shippingAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'address1' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'address2' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'validated' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hash' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currency' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventRegistryCurrency' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventRegistryCurrency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryCurrency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryCurrencyCodes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryCurrencyCodes' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryCurrency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Registry' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Registry' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nameV0204' } },
          { kind: 'Field', name: { kind: 'Name', value: 'urlV0204' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrlV0204' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wantSync' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isStandalonesType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isSyncingType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isTransferredType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transferredRegistryData' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'transferredAt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'dateString' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'lastPublicCheck' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'checkedAt' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'isPublic' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'storeName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cannotSync' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasItems' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastSyncAt' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'timestamp' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryItem' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myReservedOrders' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryOrder' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myPurchasedOrders' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryOrder' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mySession' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'email' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Date' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Date' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'timestamp' } },
          { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGroupGiftingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGetCashEquivalent' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createAt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timestamp' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'externallyOwned' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canEditProductData' } },
          { kind: 'Field', name: { kind: 'Name', value: 'storeName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productData' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'registryItem' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'brand' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'externalUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'checkoutUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'pdpUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'price' },
                  name: { kind: 'Name', value: 'priceAugmented' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'currency' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'places' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fullPrice' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'isFullPrice' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photos' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'legacyType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'fpIndex' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stillNeeded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'alreadyPurchased' } },
          { kind: 'Field', name: { kind: 'Name', value: 'alreadyReserved' } },
          { kind: 'Field', name: { kind: 'Name', value: 'totalRequested' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'donationFund' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DonationFund' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isHidden' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mustHave' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DonationFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DonationFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'donations' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DonationFundDonation' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'fundType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'goalMonetaryValue' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'suggestedDonationMonetaryValue' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'suggestedQuantityStillNeeded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'donationsAreCapped' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enforceSuggestedDonationAmount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideFundingGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'platform' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DonationFundPlatform' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photos' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'url' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'progressToGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amountTillGoalIsReached' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'progressToGoalPercent' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fpIndex' } },
          { kind: 'Field', name: { kind: 'Name', value: 'title' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enableCashOrCheck' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enableAppPlatform' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enableJoyCredit' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'mustHave' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DonationFundDonation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DonationFundDonation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderedAt' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'platformType' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DonationFundPlatform' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DonationFundPlatform' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'link' } },
          { kind: 'Field', name: { kind: 'Name', value: 'username' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPurchased' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDropship' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderTags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderedAt' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lineItems' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'LineItem' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fulfillment' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryFulfillment' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shippingAddress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'hash' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'address1' } },
                { kind: 'Field', name: { kind: 'Name', value: 'address2' } },
                { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'LineItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'LineItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'locator' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lineTags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registryItem' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lineItemData' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'donationFundPlatformType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'correctedQuantity' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'annotations' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'isEdelivery' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'frozenProductData' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customType' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'price' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'currency' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'places' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photos' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isGetCashEquivalent' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryFulfillment' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryFulfillment' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'statusPageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shipments' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryFulfillmentShipment' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryFulfillmentShipment' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryFulfillmentShipment' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'trackingInformation' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryFulfillmentShipmentTrackingInformation' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryFulfillmentShipmentTrackingInformation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryFulfillmentShipmentTrackingInformation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'company' } },
          { kind: 'Field', name: { kind: 'Name', value: 'trackingNumber' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __usePublicShopRootQuery__
 *
 * To run a query within a React component, call `usePublicShopRootQuery` and pass it any options that fit your needs.
 * When your component renders, `usePublicShopRootQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePublicShopRootQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      includeEvent: // value for 'includeEvent'
 *   },
 * });
 */
export function usePublicShopRootQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<PublicShopRootQuery, PublicShopRootQueryVariables>) {
  return ApolloReactHooks.useQuery<PublicShopRootQuery, PublicShopRootQueryVariables>(PublicShopRootDocument, baseOptions);
}
export function usePublicShopRootLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<PublicShopRootQuery, PublicShopRootQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<PublicShopRootQuery, PublicShopRootQueryVariables>(PublicShopRootDocument, baseOptions);
}
export type PublicShopRootQueryHookResult = ReturnType<typeof usePublicShopRootQuery>;
export type PublicShopRootLazyQueryHookResult = ReturnType<typeof usePublicShopRootLazyQuery>;
export type PublicShopRootQueryResult = Apollo.QueryResult<PublicShopRootQuery, PublicShopRootQueryVariables>;
